import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Paginated } from "@/jenesys-types/data/common";
import {
  CreateChaserSchema,
  CreateQuerySchema,
  DocumentQueryListSchema,
  ListQuerySchema,
  ReadQueryCommentSchema,
  ReadQuerySchema
} from "@/jenesys-types/data/communication";
import { AppError } from "@/jenesys-types/errors";

import { CompletionMeta } from "@/store/store";

export type CommunicationQueries = Paginated<ListQuerySchema>;
export type DocumentQueries = DocumentQueryListSchema[];

export interface CommunicationsHubState {
  queries: CommunicationQueries | null;
  isLoading: boolean;
  getQueriesError: AppError | null;
  isCreating: boolean;
  createQueryError: AppError | null;
  currentQuery: ReadQuerySchema | null;
  isLoadingQueryDetails: boolean;
  queryDetailsError: AppError | null;
  isPostingComment: boolean;
  postCommentError: AppError | null;
  isCreatingChaser: boolean;
  createChaserError: AppError | null;
  // Document queries state
  documentQueries: DocumentQueryListSchema[];
  isLoadingDocumentQueries: boolean;
  getDocumentQueriesError: AppError | null;
  // Resolve query state
  isResolvingQuery: boolean;
  resolveQueryError: AppError | null;
  // Delete query state
  isDeletingQuery: boolean;
  deleteQueryError: AppError | null;
  // Reassign query state
  isReassigningQuery: boolean;
  reassignQueryError: AppError | null;
}

interface LoadQueriesPayload {
  companyId: string;
  search?: string;
  queryType?: string;
  documentId?: string;
}

interface LoadDocumentQueriesPayload {
  companyId: string;
  documentId: string;
}

interface CreateQueryPayload {
  companyId: string;
  queryData: CreateQuerySchema;
  onSuccess?: (newQueryId: string) => void;
}

interface CreateChaserPayload {
  chaserData: CreateChaserSchema;
  onSuccess?: (newChaserId: string) => void;
}

interface PostQueryCommentPayload {
  companyId: string;
  queryId: string;
  comment: string;
  onSuccess?: () => void;
}

interface ResolveQueryPayload {
  companyId: string;
  queryId: string;
  onSuccess?: () => void;
}

interface DeleteQueryPayload {
  companyId: string;
  queryId: string;
  onSuccess?: () => void;
}

interface ReassignQueryPayload {
  companyId: string;
  queryId: string;
  assignedToId: string;
  onSuccess?: () => void;
}

export type LoadQueriesAction = PayloadAction<LoadQueriesPayload>;
export type CreateQueryAction = PayloadAction<CreateQueryPayload>;
export type CreateChaserAction = PayloadAction<CreateChaserPayload>;
export type LoadDocumentQueriesAction =
  PayloadAction<LoadDocumentQueriesPayload>;
export type PostQueryCommentAction = PayloadAction<PostQueryCommentPayload>;
export type ResolveQueryAction = PayloadAction<ResolveQueryPayload>;
export type DeleteQueryAction = PayloadAction<DeleteQueryPayload>;
export type ReassignQueryAction = PayloadAction<ReassignQueryPayload>;

export type LoadQueriesFailureAction = PayloadAction<
  LoadQueriesPayload,
  string,
  CompletionMeta,
  AppError
>;

export type CreateQueryFailureAction = PayloadAction<
  CreateQueryPayload,
  string,
  CompletionMeta,
  AppError
>;

export type CreateChaserFailureAction = PayloadAction<
  CreateChaserPayload,
  string,
  CompletionMeta,
  AppError
>;

export type LoadDocumentQueriesFailureAction = PayloadAction<
  LoadDocumentQueriesPayload,
  string,
  CompletionMeta,
  AppError
>;

export type PostQueryCommentFailureAction = PayloadAction<
  PostQueryCommentPayload,
  string,
  CompletionMeta,
  AppError
>;

export type ResolveQueryFailureAction = PayloadAction<
  ResolveQueryPayload,
  string,
  CompletionMeta,
  AppError
>;

export type DeleteQueryFailureAction = PayloadAction<
  DeleteQueryPayload,
  string,
  CompletionMeta,
  AppError
>;

export type ReassignQueryFailureAction = PayloadAction<
  ReassignQueryPayload,
  string,
  CompletionMeta,
  AppError
>;

export const initialState: CommunicationsHubState = {
  queries: null,
  isLoading: false,
  getQueriesError: null,
  isCreating: false,
  createQueryError: null,
  currentQuery: null,
  isLoadingQueryDetails: false,
  queryDetailsError: null,
  isPostingComment: false,
  postCommentError: null,
  isCreatingChaser: false,
  createChaserError: null,
  // Document queries state
  documentQueries: [],
  isLoadingDocumentQueries: false,
  getDocumentQueriesError: null,
  // Resolve query state
  isResolvingQuery: false,
  resolveQueryError: null,
  // Delete query state
  isDeletingQuery: false,
  deleteQueryError: null,
  // Reassign query state
  isReassigningQuery: false,
  reassignQueryError: null
};

const name = "state/features/CommunicationsHub";

export const communicationsHubSlice = createSlice({
  name,
  initialState,
  reducers: {
    loadQueries: {
      reducer: (state: CommunicationsHubState, _action: LoadQueriesAction) => {
        state.isLoading = true;
        state.getQueriesError = null;
        state.queries = null;
      },
      prepare: (
        companyId: string,
        search: string = "",
        queryType: string = "",
        documentId?: string
      ) => ({
        payload: { companyId, search, queryType, documentId }
      })
    },
    loadQueriesSuccess: {
      reducer: (
        state: CommunicationsHubState,
        action: PayloadAction<CommunicationQueries>
      ) => {
        state.isLoading = false;
        state.queries = action.payload;
      },
      prepare: (payload: CommunicationQueries) => ({
        payload
      })
    },
    loadQueriesFailure: {
      reducer: (
        state: CommunicationsHubState,
        action: LoadQueriesFailureAction
      ) => {
        state.isLoading = false;
        state.getQueriesError = action.error;
      },
      prepare: (error: AppError) => ({
        payload: {
          companyId: "",
          search: "",
          queryType: "",
          documentId: undefined
        },
        error,
        meta: { error }
      })
    },
    createQuery: {
      reducer: (state: CommunicationsHubState, _action: CreateQueryAction) => {
        state.isCreating = true;
        state.createQueryError = null;
      },
      prepare: (
        queryData: CreateQuerySchema,
        onSuccess?: (newQueryId: string) => void
      ) => ({
        payload: { companyId: queryData.company_id, queryData, onSuccess }
      })
    },
    createQuerySuccess: (state: CommunicationsHubState) => {
      state.isCreating = false;
    },
    createQueryFailure: {
      reducer: (
        state: CommunicationsHubState,
        action: CreateQueryFailureAction
      ) => {
        state.isCreating = false;
        state.createQueryError = action.error;
      },
      prepare: (error: AppError) => ({
        payload: {
          companyId: "",
          queryData: {} as CreateQuerySchema
        },
        error,
        meta: { error }
      })
    },
    loadQueryDetails: {
      reducer: (state: CommunicationsHubState) => {
        state.isLoadingQueryDetails = true;
        state.queryDetailsError = null;
        state.currentQuery = null;
      },
      prepare: (companyId: string, queryId: string) => ({
        payload: { companyId, queryId }
      })
    },
    loadQueryDetailsSuccess: {
      reducer: (
        state: CommunicationsHubState,
        action: PayloadAction<ReadQuerySchema>
      ) => {
        state.isLoadingQueryDetails = false;
        state.currentQuery = action.payload;
      },
      prepare: (payload: ReadQuerySchema) => ({ payload })
    },
    loadQueryDetailsFailure: {
      reducer: (
        state: CommunicationsHubState,
        action: PayloadAction<AppError>
      ) => {
        state.isLoadingQueryDetails = false;
        state.queryDetailsError = action.payload;
      },
      prepare: (error: AppError) => ({ payload: error })
    },
    postQueryComment: {
      reducer: (
        state: CommunicationsHubState,
        _action: PostQueryCommentAction
      ) => {
        state.isPostingComment = true;
        state.postCommentError = null;
      },
      prepare: (
        companyId: string,
        queryId: string,
        comment: string,
        onSuccess?: () => void
      ) => ({
        payload: { companyId, queryId, comment, onSuccess }
      })
    },
    postQueryCommentSuccess: {
      reducer: (
        state: CommunicationsHubState,
        action: PayloadAction<ReadQueryCommentSchema>
      ) => {
        state.isPostingComment = false;
        state.postCommentError = null;
        // Optionally, append the new comment to currentQuery.comments if currentQuery exists
        if (state.currentQuery) {
          state.currentQuery.comments = [
            ...state.currentQuery.comments,
            action.payload
          ];
        }
      },
      prepare: (payload: ReadQueryCommentSchema) => ({ payload })
    },
    postQueryCommentFailure: {
      reducer: (
        state: CommunicationsHubState,
        action: PostQueryCommentFailureAction
      ) => {
        state.isPostingComment = false;
        state.postCommentError = action.error;
      },
      prepare: (error: AppError) => ({
        payload: { companyId: "", queryId: "", comment: "" },
        error,
        meta: { error }
      })
    },
    createChaser: {
      reducer: (state: CommunicationsHubState, _action: CreateChaserAction) => {
        state.isCreatingChaser = true;
        state.createChaserError = null;
      },
      prepare: (
        chaserData: CreateChaserSchema,
        onSuccess?: (newChaserId: string) => void
      ) => ({
        payload: { chaserData, onSuccess }
      })
    },
    createChaserSuccess: (state: CommunicationsHubState) => {
      state.isCreatingChaser = false;
    },
    createChaserFailure: {
      reducer: (
        state: CommunicationsHubState,
        action: CreateChaserFailureAction
      ) => {
        state.isCreatingChaser = false;
        state.createChaserError = action.error;
      },
      prepare: (error: AppError) => ({
        payload: { chaserData: {} as CreateChaserSchema },
        error,
        meta: { error }
      })
    },
    // Document queries reducers
    loadDocumentQueries: {
      reducer: (
        state: CommunicationsHubState,
        _action: LoadDocumentQueriesAction
      ) => {
        state.isLoadingDocumentQueries = true;
        state.getDocumentQueriesError = null;
        state.documentQueries = [];
      },
      prepare: (companyId: string, documentId: string) => ({
        payload: { companyId, documentId }
      })
    },
    loadDocumentQueriesSuccess: {
      reducer: (
        state: CommunicationsHubState,
        action: PayloadAction<DocumentQueries>
      ) => {
        state.isLoadingDocumentQueries = false;
        state.documentQueries = action.payload;
      },
      prepare: (payload: DocumentQueries) => ({
        payload
      })
    },
    loadDocumentQueriesFailure: {
      reducer: (
        state: CommunicationsHubState,
        action: LoadDocumentQueriesFailureAction
      ) => {
        state.isLoadingDocumentQueries = false;
        state.getDocumentQueriesError = action.error;
      },
      prepare: (error: AppError) => ({
        payload: { companyId: "", documentId: "" },
        error,
        meta: { error }
      })
    },
    // Resolve query reducers
    resolveQuery: {
      reducer: (state: CommunicationsHubState, _action: ResolveQueryAction) => {
        state.isResolvingQuery = true;
        state.resolveQueryError = null;
      },
      prepare: (
        companyId: string,
        queryId: string,
        onSuccess?: () => void
      ) => ({
        payload: { companyId, queryId, onSuccess }
      })
    },
    resolveQuerySuccess: (state: CommunicationsHubState) => {
      state.isResolvingQuery = false;
      state.resolveQueryError = null;
      // Update the current query status to resolved if it exists
      if (state.currentQuery) {
        state.currentQuery.status = "RESOLVED";
      }
    },
    resolveQueryFailure: {
      reducer: (
        state: CommunicationsHubState,
        action: ResolveQueryFailureAction
      ) => {
        state.isResolvingQuery = false;
        state.resolveQueryError = action.error;
      },
      prepare: (error: AppError) => ({
        payload: { companyId: "", queryId: "" },
        error,
        meta: { error }
      })
    },
    // Delete query reducers
    deleteQuery: {
      reducer: (state: CommunicationsHubState, _action: DeleteQueryAction) => {
        state.isDeletingQuery = true;
        state.deleteQueryError = null;
      },
      prepare: (
        companyId: string,
        queryId: string,
        onSuccess?: () => void
      ) => ({
        payload: { companyId, queryId, onSuccess }
      })
    },
    deleteQuerySuccess: (state: CommunicationsHubState) => {
      state.isDeletingQuery = false;
      state.deleteQueryError = null;
    },
    deleteQueryFailure: {
      reducer: (
        state: CommunicationsHubState,
        action: DeleteQueryFailureAction
      ) => {
        state.isDeletingQuery = false;
        state.deleteQueryError = action.error;
      },
      prepare: (error: AppError) => ({
        payload: { companyId: "", queryId: "" },
        error,
        meta: { error }
      })
    },
    clearCurrentQuery: (state: CommunicationsHubState) => {
      state.currentQuery = null;
    },
    reassignQuery: {
      reducer: (state: CommunicationsHubState, _action: ReassignQueryAction) => {
        state.isReassigningQuery = true;
        state.reassignQueryError = null;
      },
      prepare: (
        companyId: string,
        queryId: string,
        assignedToId: string,
        onSuccess?: () => void
      ) => ({
        payload: { companyId, queryId, assignedToId, onSuccess }
      })
    },
    reassignQuerySuccess: (state: CommunicationsHubState) => {
      state.isReassigningQuery = false;
      state.reassignQueryError = null;
    },
    reassignQueryFailure: {
      reducer: (
        state: CommunicationsHubState,
        action: ReassignQueryFailureAction
      ) => {
        state.isReassigningQuery = false;
        state.reassignQueryError = action.error;
      },
      prepare: (error: AppError) => ({
        payload: { companyId: "", queryId: "", assignedToId: "" },
        error,
        meta: { error }
      })
    }
  }
});

export const {
  loadQueries,
  loadQueriesSuccess,
  loadQueriesFailure,
  createQuery,
  createQuerySuccess,
  createQueryFailure,
  loadQueryDetails,
  loadQueryDetailsSuccess,
  loadQueryDetailsFailure,
  postQueryComment,
  postQueryCommentSuccess,
  postQueryCommentFailure,
  createChaser,
  createChaserSuccess,
  createChaserFailure,
  loadDocumentQueries,
  loadDocumentQueriesSuccess,
  loadDocumentQueriesFailure,
  resolveQuery,
  resolveQuerySuccess,
  resolveQueryFailure,
  deleteQuery,
  deleteQuerySuccess,
  deleteQueryFailure,
  clearCurrentQuery,
  reassignQuery,
  reassignQuerySuccess,
  reassignQueryFailure
} = communicationsHubSlice.actions;

export const selectCommunicationsHubState = (state: {
  features: { CommunicationsHub: CommunicationsHubState };
}): CommunicationsHubState => state.features.CommunicationsHub;

export const selectQueries = createSelector(
  selectCommunicationsHubState,
  (state) => state.queries
);

export const selectIsLoading = createSelector(
  selectCommunicationsHubState,
  (state) => state.isLoading
);

export const selectGetQueriesError = createSelector(
  selectCommunicationsHubState,
  (state) => state.getQueriesError
);

export const selectIsCreating = createSelector(
  selectCommunicationsHubState,
  (state) => state.isCreating
);

export const selectCreateQueryError = createSelector(
  selectCommunicationsHubState,
  (state) => state.createQueryError
);

export const selectCurrentQuery = createSelector(
  selectCommunicationsHubState,
  (state) => state.currentQuery
);
export const selectIsLoadingQueryDetails = createSelector(
  selectCommunicationsHubState,
  (state) => state.isLoadingQueryDetails
);
export const selectQueryDetailsError = createSelector(
  selectCommunicationsHubState,
  (state) => state.queryDetailsError
);
export const selectIsPostingComment = createSelector(
  selectCommunicationsHubState,
  (state) => state.isPostingComment
);
export const selectPostCommentError = createSelector(
  selectCommunicationsHubState,
  (state) => state.postCommentError
);

export const selectIsCreatingChaser = createSelector(
  selectCommunicationsHubState,
  (state) => state.isCreatingChaser
);

export const selectCreateChaserError = createSelector(
  selectCommunicationsHubState,
  (state) => state.createChaserError
);

// Document queries selectors
export const selectDocumentQueries = createSelector(
  selectCommunicationsHubState,
  (state) => state.documentQueries
);

export const selectIsLoadingDocumentQueries = createSelector(
  selectCommunicationsHubState,
  (state) => state.isLoadingDocumentQueries
);

export const selectGetDocumentQueriesError = createSelector(
  selectCommunicationsHubState,
  (state) => state.getDocumentQueriesError
);

// Resolve query selectors
export const selectIsResolvingQuery = createSelector(
  selectCommunicationsHubState,
  (state) => state.isResolvingQuery
);

export const selectResolveQueryError = createSelector(
  selectCommunicationsHubState,
  (state) => state.resolveQueryError
);

// Delete query selectors
export const selectIsDeletingQuery = createSelector(
  selectCommunicationsHubState,
  (state) => state.isDeletingQuery
);

export const selectDeleteQueryError = createSelector(
  selectCommunicationsHubState,
  (state) => state.deleteQueryError
);

// Reassign query selectors
export const selectIsReassigningQuery = createSelector(
  selectCommunicationsHubState,
  (state) => state.isReassigningQuery
);

export const selectReassignQueryError = createSelector(
  selectCommunicationsHubState,
  (state) => state.reassignQueryError
);

export default communicationsHubSlice.reducer;
